import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type {
  DataResponse,
  ListResponse,
  NazovDielca,
  NazovDielcaCreate,
  NazovDielcaUpdate,
} from "@/api/schemas";

/** Prednastavené názvy dielcov (Nastavenia) — datalist + hrany presetu. */
export function useNazvyDielcov(): UseQueryResult<ListResponse<NazovDielca>> {
  return useQuery({
    queryKey: queryKeys.nazvyDielcov.list(),
    queryFn: async () => {
      const { data } = await api.get<ListResponse<NazovDielca>>(
        "/nazvy-dielcov",
      );
      return data;
    },
    staleTime: 5 * 60 * 1000, // menia sa zriedka (Nastavenia)
  });
}

export function useCreateNazovDielca(): UseMutationResult<
  DataResponse<NazovDielca>,
  Error,
  NazovDielcaCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NazovDielcaCreate) => {
      const { data } = await api.post<DataResponse<NazovDielca>>(
        "/nazvy-dielcov",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.nazvyDielcov.all });
    },
  });
}

export function useUpdateNazovDielca(): UseMutationResult<
  DataResponse<NazovDielca>,
  Error,
  { id: string; data: NazovDielcaUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<DataResponse<NazovDielca>>(
        `/nazvy-dielcov/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.nazvyDielcov.all });
    },
  });
}

export function useDeleteNazovDielca(): UseMutationResult<
  void,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/nazvy-dielcov/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.nazvyDielcov.all });
    },
  });
}
